// security/qrGenerator.js
// BuzzInApp — Generador de QR firmado con expiración
//
// Cada comercio tiene un QR único que:
//   - Expira en 60 segundos (configurable en security.js)
//   - Está firmado criptográficamente (Web Crypto API, SHA-256)
//   - Incluye el nombre del comercio para detectar QRs falsos pegados encima
//   - No puede ser reutilizado (timestamp en el payload)
//
// El QR contiene un JSON stringificado y codificado en base64url:
//   { bid, bname, token, issued, expires, sig }

import { QR_SECURITY } from "../config/security";

// ─── Helpers criptográficos ───────────────────────────────────────────────────

/**
 * Genera N bytes aleatorios y los devuelve como string hexadecimal.
 * @param {number} bytes
 * @returns {string}
 */
const randomHex = (bytes = QR_SECURITY.TOKEN_BYTES) => {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Codifica un string en base64url (sin padding, URL-safe).
 * @param {string} str
 * @returns {string}
 */
const toBase64url = (str) =>
  btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

/**
 * Decodifica un string base64url.
 * @param {string} b64
 * @returns {string}
 */
const fromBase64url = (b64) => {
  const padded = b64.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  return decodeURIComponent(escape(atob(padded + pad)));
};

/**
 * Genera un digest SHA-256 del payload y lo devuelve como hex.
 * Usa la Web Crypto API (disponible en todos los browsers modernos y PWA).
 * @param {string} payload
 * @returns {Promise<string>}
 */
const sha256hex = async (payload) => {
  const encoder = new TextEncoder();
  const data    = encoder.encode(payload);
  const hashBuf = await crypto.subtle.digest(QR_SECURITY.HASH_ALGORITHM, data);
  return Array.from(new Uint8Array(hashBuf), (b) =>
    b.toString(16).padStart(2, "0")
  ).join("");
};

// ─── Generación de QR payload ────────────────────────────────────────────────

/**
 * Genera el payload firmado para el QR de un comercio.
 *
 * @param {object} params
 * @param {string} params.businessId    ID del comercio (de Google Sheet / Firestore)
 * @param {string} params.businessName  Nombre del comercio (aparece en pantalla al escanear)
 * @param {string} [params.secret]      Secreto del comercio (guardado en Firestore, no en Sheet)
 *                                      Si no se provee, se usa un secreto derivado del ID.
 * @returns {Promise<{ qrString: string, token: string, expires: number }>}
 */
export const generateQRPayload = async ({
  businessId,
  businessName,
  secret = null,
}) => {
  const now     = Date.now();
  const expires = now + QR_SECURITY.TOKEN_EXPIRY_SECONDS * 1000;
  const token   = QR_SECURITY.TOKEN_PREFIX + randomHex();

  // El secreto real debe venir de Firestore (campo privado del comercio).
  // Si no está disponible aún (dev/test), derivamos uno del ID + una salt fija.
  const signingKey = secret ?? `buz_dev_${businessId}_salt_2026`;

  // Payload que se firma: campos que no pueden alterarse sin invalidar la firma
  const sigInput = `${token}|${businessId}|${expires}|${signingKey}`;
  const sig      = await sha256hex(sigInput);

  const payload = {
    bid:    businessId,
    bname:  businessName,  // visible en pantalla → detecta QRs falsos pegados encima
    token,
    issued: now,
    expires,
    sig,
  };

  const qrString = toBase64url(JSON.stringify(payload));

  return { qrString, token, expires };
};

// ─── Verificación del payload ─────────────────────────────────────────────────

/**
 * Verifica si un payload de QR es válido.
 *
 * @param {string} qrString   String leído por el scanner (base64url)
 * @param {string} [secret]   Secreto del comercio (recuperado de Firestore)
 * @returns {Promise<{ valid: boolean, reason: string, payload: object|null }>}
 */
export const verifyQRPayload = async (qrString, secret = null) => {
  let payload;

  // 1. Decodificar
  try {
    payload = JSON.parse(fromBase64url(qrString));
  } catch {
    return { valid: false, reason: "invalid_format", payload: null };
  }

  // 2. Campos obligatorios
  const { bid, bname, token, issued, expires, sig } = payload;
  if (!bid || !bname || !token || !issued || !expires || !sig) {
    return { valid: false, reason: "missing_fields", payload: null };
  }

  // 3. Prefijo del token
  if (!token.startsWith(QR_SECURITY.TOKEN_PREFIX)) {
    return { valid: false, reason: "invalid_token_prefix", payload: null };
  }

  // 4. Expiración
  if (Date.now() > expires) {
    return { valid: false, reason: "expired", payload };
  }

  // 5. Firma
  const signingKey = secret ?? `buz_dev_${bid}_salt_2026`;
  const sigInput   = `${token}|${bid}|${expires}|${signingKey}`;
  const expectedSig = await sha256hex(sigInput);

  if (sig !== expectedSig) {
    return { valid: false, reason: "invalid_signature", payload };
  }

  return { valid: true, reason: "ok", payload };
};

// ─── Tiempo restante ──────────────────────────────────────────────────────────

/**
 * Retorna los segundos restantes hasta que expire un payload.
 * Útil para mostrar el countdown en QRDisplay.js
 * @param {number} expires  timestamp en ms
 * @returns {number}  segundos restantes (0 si ya expiró)
 */
export const secondsUntilExpiry = (expires) =>
  Math.max(0, Math.floor((expires - Date.now()) / 1000));
